import {GLOBAL} from "../../../common/globals";
import {Resolution} from "../../../common/resolution";
import {resolve} from "../../../container";
import {forAllEntries, IntersectionObserverFactory} from "../../../common/observation";
import {FeatureDetector} from "../../../common/featureDetector";
import {schedule, SingletonPromise} from "../../../common/utils/promises";
import {ManagingResources} from "../../../common/lifetime";
import {Page} from "../../../common/page";
import type {Maybe} from "../../../bootstrap/common/types";
import {HeroTeaserImageReadyHandler} from "./HeroTeaserImageReadyHandler";

// Heights are derived from common resolutions
const QXGA_VIEWPORT_HEIGHT = 1536;
const XGA_VIEWPORT_HEIGHT = 768;
// This function is used to calculate the factor for the parallax effect. The larger the viewport, the larger the factor.
const FACTOR_FUNCTION = (x: number): number => (XGA_VIEWPORT_HEIGHT + x) / (2 * QXGA_VIEWPORT_HEIGHT);

class ParallaxElement {
    private isInViewport: boolean;
    private parallax: Parallax;
    private intersectionObserver: Maybe<IntersectionObserver>;
    private element: HTMLElement;
    private container: HTMLElement;
    private singletonPromise: SingletonPromise<void>;

    public constructor(
        element: HTMLElement,
        container: HTMLElement,
        private intersectionObserverFactory: IntersectionObserverFactory = resolve(IntersectionObserverFactory),
        private page: Page = resolve(Page)
    ) {
        this.singletonPromise = new SingletonPromise<void>();
        this.element = element;
        this.container = container;
        this.parallax = new Parallax();

        this.init();
    }

    public disconnect(): void {
        this.intersectionObserver?.disconnect();
    }

    public update(): void {
        this.updateBoundaries();
        if (!this.isInViewport) {
            return;
        }
        this.updateCss();
    }

    private init(): void {
        this.intersectionObserver = this.intersectionObserverFactory.create(forAllEntries(entry => {
            this.isInViewport = entry.isIntersecting;
            this.toggle();
        }));
        this.intersectionObserver.observe(this.container);

        this.updateBoundaries();
    }

    private updateBoundaries(): void {
        this.parallax.updateBoundaries({
            containerHeight: this.container.outerHeight(),
            elementHeight: this.element.outerHeight(),
            windowHeight: this.page.viewportHeight()
        });
    }

    private toggle(): void {
        if (this.isInViewport) {
            this.listenToScroll();
        } else {
            this.pause();
        }
    }

    private pause(): void {
        GLOBAL.window().removeEventListener("scroll", this.updateCss);
    }

    private listenToScroll(): void {
        this.updateCss();
        GLOBAL.window().addEventListener("scroll", this.updateCss);
    }

    private updateCss = (): void => {
        this.singletonPromise.of(() => schedule(new Promise<void>((res, _) =>
            GLOBAL.window().requestAnimationFrame(() => {
                const translation = this.parallax.computeCurrentTranslation(this.container.topOffset(), this.page.getYScrollPosition());
                this.element.style.transform = `translate3d(0,${translation}px,0)`;
                res();
            }))).as("parallax"));
    };
}

type ParallaxBoundaries = {
    elementHeight: number;
    containerHeight: number;
    windowHeight: number;
};

export class Parallax {
    private translationRange: number;
    private scrollRange: number;

    public constructor() {
        this.translationRange = 1;
        this.scrollRange = 1;
    }

    public computeCurrentTranslation(containerOffsetTop: number, scrollY: number): string {
        const relativeViewportPosition = containerOffsetTop - scrollY;
        const scrollPercentage = 1 - relativeViewportPosition / this.scrollRange;

        return (this.translationRange * scrollPercentage).toFixed(2);
    }

    public updateBoundaries(boundaries: ParallaxBoundaries): void {
        const elementHeight = boundaries.elementHeight;
        const containerHeight = boundaries.containerHeight;
        const windowHeight = boundaries.windowHeight;

        if (elementHeight <= windowHeight) {
            this.translationRange = (elementHeight - containerHeight) * FACTOR_FUNCTION(windowHeight - elementHeight);
        } else {
            this.translationRange = (windowHeight - containerHeight) / 4;
        }
        this.scrollRange = (windowHeight - containerHeight) / 2;
    }
}

export class EopParallaxTeaser extends ManagingResources(HTMLElement) {

    private instance: Maybe<ParallaxElement>;

    public constructor(
        private resolution: Resolution = resolve(Resolution),
        private features: FeatureDetector = resolve(FeatureDetector)
    ) {
        super();
    }

    public connectedCallback(): void {
        if (this.features.isTouchDevice()) {
            return;
        }
        const parallaxElement = this.querySelector<HTMLElement>(".parallax-layer")!;
        this.instance = new ParallaxElement(parallaxElement, this);
        this.resolution.onWindowResize(() => this.instance?.update(), this);

        new HeroTeaserImageReadyHandler(".hero-teaser-image").installOn(this);
    }

    public disconnectedCallback(): void {
        this.instance?.disconnect();
    }
}

customElements.define("eop-parallax-teaser", EopParallaxTeaser);
